import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  crmService,
  Client,
  ClientStats,
  DashboardKpis,
  DashboardSections,
  SalesDashboardAttentionItem,
  TodayFeedItem,
  TodayCounts,
  WorkflowSuggestion,
} from '../services/crmService';
import type { AttentionItem } from '../components/dashboard/DashboardAttentionSection';
import { analyticsService, type ScoringPerformanceResponse } from '../services/analyticsService';
import KpiStatCard from '../components/dashboard/KpiStatCard';
import FunnelOverviewCard from '../components/dashboard/FunnelOverviewCard';
import DashboardAttentionSection from '../components/dashboard/DashboardAttentionSection';
import RankedPipelineList from '../components/dashboard/RankedPipelineList';
import DashboardSection from '../components/dashboard/DashboardSection';
import DashboardTier from '../components/dashboard/DashboardTier';
import TodayHero from '../components/dashboard/TodayHero';
import DashboardActionsMenu from '../components/dashboard/DashboardActionsMenu';
import LeadTemperatureChart from '../components/dashboard/LeadTemperatureChart';
import DashboardCard from '../components/dashboard/DashboardCard';
import {
  UsersIcon,
  TargetIcon,
  CheckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
} from '../components/Icons';
import { leadsPipelineUrl, leadsQuickCaptureUrl } from '../utils/crmLinks';
import QuickCaptureFab from '../components/crm/QuickCaptureFab';
import ScoringPerformancePanel from '../components/scoring/ScoringPerformancePanel';
import { dashboardService, type TodayPayload } from '../services/dashboardService';

function formatMoney(val: number | string | undefined) {
  const n = typeof val === 'number' ? val : parseFloat(val ?? '0');
  if (Number.isNaN(n)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function mapAttentionItems(
  items: SalesDashboardAttentionItem[],
  suggestions: WorkflowSuggestion[],
  extras: AttentionItem[]
): AttentionItem[] {
  const mapped: AttentionItem[] = items.map((i) => ({
    id: i.id,
    title: i.title,
    description: i.detail,
    to: i.href || '/leads',
    ctaLabel: 'Open',
    tone: i.severity === 'high' ? 'critical' : i.severity === 'medium' ? 'warning' : 'default',
  }));
  const sug = suggestions.map((s) => ({
    id: s.id,
    title: s.title,
    description: s.detail,
    to: s.href || '/leads',
    ctaLabel: 'Continue',
    tone: 'default' as const,
  }));
  return [...mapped, ...sug, ...extras];
}

const SalesDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [today, setToday] = useState<TodayFeedItem[]>([]);
  const [todayCounts, setTodayCounts] = useState<TodayCounts | null>(null);
  const [kpis, setKpis] = useState<DashboardKpis | null>(null);
  const [sections, setSections] = useState<DashboardSections | null>(null);
  const [clientStats, setClientStats] = useState<ClientStats | null>(null);
  const [workflowSuggestions, setWorkflowSuggestions] = useState<WorkflowSuggestion[]>([]);
  const [scoringPerformance, setScoringPerformance] = useState<ScoringPerformanceResponse | null>(null);
  const [scoringError, setScoringError] = useState('');
  const [scoringAvailable, setScoringAvailable] = useState(true);
  const [todayPayload, setTodayPayload] = useState<TodayPayload | null>(null);
  const [todayLoading, setTodayLoading] = useState(true);
  const [todayError, setTodayError] = useState('');

  const loadToday = async () => {
    try {
      setTodayLoading(true);
      setTodayError('');
      const payload = await dashboardService.getToday();
      setTodayPayload(payload);
    } catch (e) {
      setTodayError(e instanceof Error ? e.message : 'Failed to load metrics');
    } finally {
      setTodayLoading(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [dash, scoringRes, statsRes] = await Promise.all([
        crmService.getSalesDashboard(),
        // 401/403 means the plan doesn't include advanced analytics — hide the
        // section rather than render an all-zero chart that reads as broken.
        analyticsService.getScoringPerformance().then(
          (res) => ({ ok: true as const, res }),
          (err: unknown) => ({ ok: false as const, err })
        ),
        crmService.getClientStats().catch(() => null),
      ]);
      setToday(dash.today ?? []);
      setTodayCounts(dash.today_counts ?? null);
      setKpis(dash.kpis ?? null);
      setSections(dash.sections ?? null);
      setWorkflowSuggestions(dash.workflow_suggestions ?? []);
      if (scoringRes.ok) {
        setScoringPerformance(scoringRes.res);
        setScoringAvailable(true);
        setScoringError('');
      } else {
        const status = (scoringRes.err as { status?: number })?.status;
        setScoringPerformance(null);
        setScoringAvailable(status !== 401 && status !== 403 && status !== 404);
        setScoringError(scoringRes.err instanceof Error ? scoringRes.err.message : 'Failed to load scoring performance');
      }
      setClientStats(statsRes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    loadToday();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pipelineTotal = kpis
    ? kpis.leads_in_pipeline + kpis.prospects + kpis.clients
    : 0;
  const showOnboarding = !loading && kpis !== null && pipelineTotal === 0;

  const funnelStages = kpis
    ? [
        {
          key: 'leads',
          label: 'Leads',
          value: kpis.leads_in_pipeline,
          displayValue: String(kpis.leads_in_pipeline),
        },
        {
          key: 'prospects',
          label: 'Prospects',
          value: kpis.prospects,
          displayValue: String(kpis.prospects),
        },
        {
          key: 'clients',
          label: 'Clients',
          value: kpis.clients,
          displayValue: String(kpis.clients),
        },
      ]
    : [];

  const maxStage = Math.max(
    kpis?.leads_in_pipeline ?? 0,
    kpis?.prospects ?? 0,
    kpis?.clients ?? 0,
    1
  );
  const stepRates: (number | null)[] = [];
  if (kpis && maxStage > 0) {
    const l = kpis.leads_in_pipeline;
    const p = kpis.prospects;
    const c = kpis.clients;
    stepRates.push(l > 0 ? (p / l) * 100 : null);
    stepRates.push(p > 0 ? (c / p) * 100 : null);
  } else {
    stepRates.push(null, null);
  }

  const revenueBooked = kpis?.revenue_booked ?? 0;
  const revenueCollected = kpis?.revenue_collected_30d ?? 0;

  const attentionExtras = useMemo((): AttentionItem[] => {
    if (kpis && pipelineTotal > 0 && revenueBooked === 0 && revenueCollected === 0) {
      return [
        {
          id: 'no_revenue_recorded',
          title: 'No revenue recorded yet',
          description:
            'Log booked and collected revenue as deals close so you can tie conversion back to sources and pipeline work.',
          to: '/clients',
          ctaLabel: 'View clients',
          tone: 'warning',
        },
      ];
    }
    return [];
  }, [kpis, pipelineTotal, revenueBooked, revenueCollected]);

  const mergedAttention = useMemo(() => {
    const items = sections?.attention?.items ?? [];
    return mapAttentionItems(items, workflowSuggestions, attentionExtras);
  }, [sections, workflowSuggestions, attentionExtras]);

  const bestBooked =
    sections?.revenue_by_source?.items && sections.revenue_by_source.items.length > 0
      ? sections.revenue_by_source.items.reduce((a, b) =>
          parseFloat(b.revenue) > parseFloat(a.revenue) ? b : a
        )
      : null;

  const hotLeads = (sections?.hot_leads?.items ?? []) as Client[];
  const recentlyContacted = (sections?.recently_contacted?.items ?? []) as Client[];
  const demosThisWeek = (sections?.demos_this_week?.items ?? []) as Client[];

  const leadSources = sections?.lead_sources;
  const showSourcesSection = Boolean(
    leadSources?.has_data ||
      sections?.website_traffic?.has_data ||
      sections?.revenue_by_source?.has_data
  );

  const funnelCompact = sections?.pipeline_funnel?.all_zero_after_leads ?? false;

  const quickAddHref = '/leads?add=full';

  return (
    <div className="space-y-8 max-w-[1400px] mx-auto overflow-x-hidden">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between border-b border-border pb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-fg">Dashboard</h1>
        </div>
        <nav className="flex flex-wrap gap-2 shrink-0" aria-label="Quick actions">
          <Link
            to={leadsQuickCaptureUrl()}
            className="btn-primary text-sm py-2 px-3 inline-flex items-center justify-center rounded-md"
          >
            Photo capture
          </Link>
          <Link
            to={quickAddHref}
            className="btn-secondary text-sm py-2 px-3 inline-flex items-center justify-center rounded-md"
          >
            Quick Add
          </Link>
          <DashboardActionsMenu />
        </nav>
      </header>

      {error ? <div className="alert-danger rounded-md px-4 py-3 text-sm">{error}</div> : null}

      <DashboardTier label="Act now">
        <TodayHero
          items={today}
          counts={todayCounts}
          loading={loading}
          onQuickAdd={() => {
            window.location.href = quickAddHref;
          }}
        />

        {showOnboarding ? (
          <section
            className="rounded-md border border-dashed border-border bg-surface p-5"
            aria-label="Get started"
          >
            <h3 className="text-base font-semibold text-fg">Activate your pipeline</h3>
            <p className="text-sm text-fg-muted mt-1 max-w-2xl">
              No leads yet — use Photo capture on the Leads page (camera button) for a fast photo, add a lead manually,
              import a CSV, or connect your site so forms flow in automatically.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to={leadsQuickCaptureUrl()} className="btn-primary text-sm py-2 px-4 rounded-md">
                Photo capture
              </Link>
              <Link to={quickAddHref} className="btn-secondary text-sm py-2 px-4 rounded-md">
                Quick Add
              </Link>
            </div>
          </section>
        ) : null}
      </DashboardTier>

      <DashboardTier label="Pipeline health">
        {/* ── Stale activity strip ──────────────────────────────────────────── */}
        {!todayLoading && todayPayload?.stale_activity.show ? (
          <div
            role="alert"
            className="rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-fg flex items-center justify-between gap-3"
          >
            <span>
              {todayPayload.stale_activity.days === null
                ? 'No outreach logged yet — log a call, email, or note to stay on top of your pipeline.'
                : `No outreach in ${todayPayload.stale_activity.days} day${todayPayload.stale_activity.days === 1 ? '' : 's'} — log a call, email, or note.`}
            </span>
            <Link to="/leads" className="shrink-0 text-primary font-medium hover:underline">
              Go to leads
            </Link>
          </div>
        ) : null}

        {/* ── Row 1: actionable pipeline metrics from /api/dashboard/today/ ── */}
        {todayLoading ? (
          <div className="grid grid-cols-12 gap-4" aria-busy="true" aria-label="Loading pipeline metrics">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="col-span-12 sm:col-span-6 lg:col-span-3 bg-surface border border-border rounded-lg h-32 animate-pulse"
              />
            ))}
          </div>
        ) : todayError ? (
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-4">
              {(['Contactable now', 'Due today', 'New this week', 'Conversion (30d)'] as const).map(
                (title) => (
                  <KpiStatCard
                    key={title}
                    className="col-span-12 sm:col-span-6 lg:col-span-3"
                    title={title}
                    value="—"
                  />
                )
              )}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-danger">Failed to load metrics.</span>
              <button
                type="button"
                onClick={() => void loadToday()}
                className="btn-secondary text-xs px-3 py-1 rounded-md"
              >
                Retry
              </button>
            </div>
          </div>
        ) : todayPayload ? (
          <section aria-label="Today's actionable metrics">
            <h3 className="sr-only">Pipeline metrics</h3>
            <div className="grid grid-cols-12 gap-4">
              <KpiStatCard
                className="col-span-12 sm:col-span-6 lg:col-span-3"
                title="Contactable now"
                value={todayPayload.contactable.total}
                hint={`${todayPayload.contactable.callable} callable · ${todayPayload.contactable.emailable} emailable`}
                link={todayPayload.contactable.filter_url}
                icon={<UsersIcon className="h-5 w-5 text-primary" />}
              />
              <KpiStatCard
                className="col-span-12 sm:col-span-6 lg:col-span-3"
                title="Due today"
                value={todayPayload.due_today.total}
                hint={`${todayPayload.due_today.scheduled} scheduled · ${todayPayload.due_today.first_touch} first touch`}
                link={todayPayload.due_today.filter_url}
                icon={<TargetIcon className="h-5 w-5 text-primary" />}
              />
              <KpiStatCard
                className="col-span-12 sm:col-span-6 lg:col-span-3"
                title="New this week"
                value={todayPayload.new_this_week.count}
                hint={
                  todayPayload.new_this_week.delta > 0
                    ? `+${todayPayload.new_this_week.delta} vs last week`
                    : todayPayload.new_this_week.delta < 0
                    ? `${todayPayload.new_this_week.delta} vs last week`
                    : 'Same as last week'
                }
                link={todayPayload.new_this_week.filter_url}
                icon={<ChartBarIcon className="h-5 w-5 text-primary" />}
              />
              <KpiStatCard
                className="col-span-12 sm:col-span-6 lg:col-span-3"
                title="Conversion (30d)"
                value={
                  todayPayload.conversion.rate !== null
                    ? `${todayPayload.conversion.rate}%`
                    : 'Not enough data'
                }
                hint={
                  todayPayload.conversion.rate !== null
                    ? `${todayPayload.conversion.converted} of ${todayPayload.conversion.cohort} (30d)`
                    : `${todayPayload.conversion.converted} of ${todayPayload.conversion.cohort} · need 10+`
                }
                link={todayPayload.conversion.filter_url}
                icon={<CheckIcon className="h-5 w-5 text-primary" />}
                iconTintClass={
                  todayPayload.conversion.rate !== null
                    ? 'bg-success/20 text-success'
                    : undefined
                }
              />
            </div>
          </section>
        ) : null}

        <LeadTemperatureChart
          hot={clientStats?.leads_hot ?? 0}
          warm={clientStats?.leads_warm ?? 0}
          cold={clientStats?.leads_cold ?? 0}
          loading={loading}
        />

        {kpis && (revenueBooked > 0 || revenueCollected > 0) ? (
          <section aria-label="Business snapshot">
            <h3 className="sr-only">At a glance</h3>
            {/* Visitors / Prospects / Clients intentionally live elsewhere: visitors in
                Sources & traction, prospects + clients in the pipeline funnel below. */}
            <div className="grid grid-cols-12 gap-4">
              {revenueBooked > 0 ? (
                <KpiStatCard
                  className="col-span-12 sm:col-span-6 lg:col-span-3"
                  title="Revenue booked (30d)"
                  value={formatMoney(revenueBooked)}
                  hint="Committed deals"
                  loading={loading}
                  icon={<CurrencyDollarIcon className="h-5 w-5 text-primary" />}
                />
              ) : null}
              {revenueCollected > 0 ? (
                <KpiStatCard
                  className="col-span-12 sm:col-span-6 lg:col-span-3"
                  title="Collected (30d)"
                  value={formatMoney(revenueCollected)}
                  hint="Cash received"
                  loading={loading}
                  variant="secondary"
                  icon={<CurrencyDollarIcon className="h-5 w-5 text-primary" />}
                />
              ) : null}
            </div>
          </section>
        ) : null}

        <DashboardSection show={sections?.pipeline_funnel?.has_data ?? false}>
          <FunnelOverviewCard
            title="Pipeline overview"
            subtitle="Leads → prospects → clients. Step rates show how contacts advance; revenue reflects your last 30 days."
            stages={funnelStages}
            stepRates={stepRates}
            variant={funnelCompact ? 'compact' : 'default'}
            footer={
              revenueBooked > 0 || revenueCollected > 0 ? (
                <span className="tabular-nums">
                  Booked {formatMoney(revenueBooked)} · Collected {formatMoney(revenueCollected)}
                </span>
              ) : funnelCompact ? undefined : (
                <div>
                  <p className="text-xs text-fg-muted">
                    When you record revenue, it appears here alongside pipeline stages — no extra analytics overhead.
                  </p>
                </div>
              )
            }
          />
        </DashboardSection>
      </DashboardTier>

      <DashboardTier label="Working queues">
        {(() => {
          // Only present cards enter the grid, so a hidden queue never leaves an empty column.
          const cards: React.ReactNode[] = [];
          if (sections?.hot_leads?.has_data) {
            cards.push(
              <RankedPipelineList
                key="hot"
                title="Hot leads"
                viewAllTo={leadsPipelineUrl({ temperature: 'hot' })}
                viewAllLabel="All hot leads"
                clients={hotLeads.slice(0, 5)}
                loading={loading}
                emptyTitle="No hot priorities"
                emptyDescription="Mark temperature on leads as you learn more — hot/warm/cold drives this queue."
                emptyCtaLabel="Filter hot on Leads"
                emptyCtaTo={leadsPipelineUrl({ temperature: 'hot' })}
                variant="lead"
                showNextAction
                detailHrefForClient={(c) => leadsPipelineUrl({ pipeline_contact: c.id, temperature: 'hot' })}
              />
            );
          }
          if (sections?.recently_contacted?.has_data) {
            cards.push(
              <RankedPipelineList
                key="recent"
                title="Recently contacted"
                viewAllTo={leadsPipelineUrl({ ordering: '-last_contacted' })}
                viewAllLabel="Sort by last contact"
                clients={recentlyContacted.slice(0, 3)}
                loading={loading}
                emptyTitle="No touches logged yet"
                emptyDescription="Log a call, email, or text from the lead profile to build momentum."
                emptyCtaLabel="Open Leads"
                emptyCtaTo="/leads"
                variant="lead"
                showNextAction
              />
            );
          }
          if (sections?.demos_this_week?.has_data) {
            cards.push(
              <RankedPipelineList
                key="demos"
                title="Demos this week"
                viewAllTo={leadsPipelineUrl({ demo_week: true })}
                viewAllLabel="View demos"
                clients={demosThisWeek.slice(0, 3)}
                loading={loading}
                emptyTitle="No demos scheduled"
                emptyDescription="When you set demo dates on leads, they'll show in this week's view."
                emptyCtaLabel="View demos this week"
                emptyCtaTo={leadsPipelineUrl({ demo_week: true })}
                variant="lead"
                showNextAction
              />
            );
          }
          if (sections?.attention?.has_data) {
            cards.push(
              <DashboardAttentionSection
                key="attn"
                items={mergedAttention}
                heading="What needs attention"
                intro="Short recommendations from scores, temperature, follow-up dates, and pipeline health."
              />
            );
          }
          if (cards.length === 0) return null;
          return (
            <div className="grid grid-cols-12 gap-4">
              {cards.map((card, i) => {
                const lgSpan =
                  cards.length % 2 === 1 && i === cards.length - 1 ? 'lg:col-span-12' : 'lg:col-span-6';
                return (
                  <div key={i} className={`col-span-12 ${lgSpan}`}>
                    {card}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </DashboardTier>

      <DashboardTier label="Context & traction">
        <DashboardSection
          show={showSourcesSection}
          teaser={
            <>
              <span className="font-medium text-fg">Sources &amp; traction</span> — Add leads, connect website tracking,
              or log revenue to unlock this section without extra dashboards.
            </>
          }
        >
          <DashboardCard
            title="Sources &amp; traction"
            subtitle="Where your leads come from, plus traffic and revenue context."
          >
            {leadSources?.has_data ? (
              <div className="mb-4 rounded-md border border-border p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="flex items-center gap-2 text-fg font-medium text-sm">
                    <TargetIcon className="h-5 w-5 text-primary" />
                    Lead sources
                  </div>
                  <span className="text-xs text-fg-muted">
                    {leadSources.total} lead{leadSources.total === 1 ? '' : 's'}
                    {leadSources.window_days ? ` · last ${leadSources.window_days} days` : ' · all time'}
                  </span>
                </div>
                <ul className="mt-3 space-y-2">
                  {leadSources.items.map((row) => (
                    <li key={row.key} className="flex items-center gap-3">
                      <span className="w-28 shrink-0 truncate text-sm text-fg-secondary" title={row.label}>
                        {row.label}
                      </span>
                      <span
                        className="h-2 flex-1 overflow-hidden rounded-full bg-surface-muted"
                        role="img"
                        aria-label={`${row.label}: ${row.count} of ${leadSources.total} leads (${row.percent}%)`}
                      >
                        <span
                          className="block h-full rounded-full bg-primary"
                          style={{ width: `${Math.max(row.percent, 2)}%` }}
                        />
                      </span>
                      <span className="w-24 shrink-0 text-right text-sm tabular-nums text-fg">
                        {row.count} <span className="text-fg-muted">({row.percent}%)</span>
                      </span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={leadsPipelineUrl({})}
                  className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
                >
                  View pipeline
                </Link>
              </div>
            ) : null}
            <div className="grid gap-4 md:grid-cols-2">
              {sections?.website_traffic?.has_data ? (
                <div className="rounded-md border border-border p-4">
                  <div className="flex items-center gap-2 text-fg font-medium text-sm">
                    <ChartBarIcon className="h-5 w-5 text-primary" />
                    Website traffic
                  </div>
                  <p className="mt-2 text-sm text-fg-muted">
                    Visitors (rolling 30d + today, unique sessions):{' '}
                    <span className="font-medium text-fg">{sections.website_traffic.visitors_30d}</span>
                  </p>
                  <Link to="/settings/website" className="mt-3 inline-block text-sm font-medium text-primary hover:underline">
                    Website settings
                  </Link>
                </div>
              ) : null}
              {sections?.revenue_by_source?.has_data && bestBooked ? (
                <div className="rounded-md border border-border p-4">
                  <div className="flex items-center gap-2 text-fg font-medium text-sm">
                    <CurrencyDollarIcon className="h-5 w-5 text-primary" />
                    Top booked source (30d)
                  </div>
                  <p className="mt-2 text-sm text-fg-muted">
                    <span className="font-medium text-fg">{bestBooked.source}</span>{' '}
                    <span className="tabular-nums">({formatMoney(bestBooked.revenue)})</span>
                  </p>
                </div>
              ) : null}
            </div>
          </DashboardCard>
        </DashboardSection>
      </DashboardTier>

      <QuickCaptureFab onCreated={() => void load()} />

      {scoringAvailable ? (
        <DashboardTier label="Scoring performance">
          <ScoringPerformancePanel loading={loading} error={scoringError} data={scoringPerformance} />
        </DashboardTier>
      ) : null}
    </div>
  );
};

export default SalesDashboard;
